import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface UIState {
  theme: 'light' | 'dark' | 'auto'
  language: 'zh-CN' | 'en-US'
  sidebarCollapsed: boolean
  notifications: Array<{
    id: string
    type: 'info' | 'success' | 'warning' | 'error'
    title: string
    message: string
    timestamp: string
    read: boolean
  }>
  loading: boolean
  modal: {
    open: boolean
    type: string | null
    data: any
  }
  toast: {
    open: boolean
    type: 'success' | 'error' | 'info' | 'warning'
    message: string
  }
}

const SYSTEM_STORAGE_KEY = 'asset_settings_system'
function getInitialTheme(): 'light' | 'dark' | 'auto' {
  try {
    const sys = localStorage.getItem(SYSTEM_STORAGE_KEY)
    if (sys) {
      const data = JSON.parse(sys) as { theme?: string }
      if (data.theme === 'light' || data.theme === 'dark' || data.theme === 'auto') return data.theme
    }
    const t = localStorage.getItem('theme')
    if (t === 'light' || t === 'dark' || t === 'auto') return t
  } catch {}
  return 'light'
}

export const initialTheme = getInitialTheme()

const initialState: UIState = {
  theme: initialTheme,
  language: 'zh-CN',
  sidebarCollapsed: false,
  notifications: [
    {
      id: '1',
      type: 'info',
      title: '系统更新',
      message: '系统将在今晚24:00进行维护更新',
      timestamp: '2024-01-15 10:30',
      read: false,
    },
    {
      id: '2',
      type: 'success',
      title: '数据导入成功',
      message: '成功导入1000条交易记录',
      timestamp: '2024-01-15 09:15',
      read: true,
    },
    {
      id: '3',
      type: 'warning',
      title: '资产预警',
      message: '数字货币资产波动较大，请注意风险',
      timestamp: '2024-01-14 16:45',
      read: false,
    },
    {
      id: '4',
      type: 'error',
      title: '连接超时',
      message: '数据源连接超时，正在重试...',
      timestamp: '2024-01-14 14:20',
      read: true,
    },
  ],
  loading: false,
  modal: {
    open: false,
    type: null,
    data: null,
  },
  toast: {
    open: false,
    type: 'info',
    message: '',
  },
}

export const uiInitialState = initialState

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light'
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'auto'>) => {
      state.theme = action.payload
    },
    setLanguage: (state, action: PayloadAction<'zh-CN' | 'en-US'>) => {
      state.language = action.payload
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
    },
    addNotification: (state, action: PayloadAction<UIState['notifications'][0]>) => {
      state.notifications.unshift(action.payload)
      // 保持最多50条通知
      if (state.notifications.length > 50) {
        state.notifications.pop()
      }
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true
      })
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearAllNotifications: (state) => {
      state.notifications = []
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
      state.modal = {
        open: true,
        type: action.payload.type,
        data: action.payload.data || null,
      }
    },
    closeModal: (state) => {
      state.modal = {
        open: false,
        type: null,
        data: null,
      }
    },
    showToast: (state, action: PayloadAction<{ type: UIState['toast']['type']; message: string }>) => {
      state.toast = {
        open: true,
        type: action.payload.type,
        message: action.payload.message,
      }
    },
    hideToast: (state) => {
      state.toast.open = false
    },
  },
})

export const {
  toggleTheme,
  setTheme,
  setLanguage,
  toggleSidebar,
  setSidebarCollapsed,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  clearAllNotifications,
  setLoading,
  openModal,
  closeModal,
  showToast,
  hideToast,
} = uiSlice.actions

export default uiSlice.reducer